import {
  HEADER_META_PT,
  documentFooterEnabled,
  documentHeaderVisibility,
} from './document-header.js';
import { highlightedLineKeys } from './export-annotations.js';
import {
  CSS_PX_PER_PT,
  DEFAULT_COLUMN_WIDTH_PX,
  DEFAULT_ROW_HEIGHT_PX,
  DENSITY_PADDING_PX,
  FOOTER_GAP_PX,
  FOOTER_RESERVED_PX,
  HEADER_ROW_RESERVED_PX,
  LINE_HEIGHT_FACTOR,
  ROW_LABEL_WIDTH_PX,
} from './page-geometry.js';
import {
  BORDER_COLOR,
  BORDER_STYLE,
  DOCUMENT_HEADER_CSS,
  escapeHtml,
  formatPx,
  pmsSignatureHtml,
  renderColumnHeaderRow,
  renderDataCells,
  renderDocumentHeaderContent,
  renderGridColgroup,
  renderHtmlDocument,
  renderRowLabelCells,
  renderSubjectDescriptionBlock,
  renderTransposedSectionHtml,
} from './render-export-html-parts.js';
import type { RenderExportHtmlInput } from './render-export-html.js';
import { notesStyle, renderNotesSectionHtml } from './render-export-notes.js';
import { TITILLIUM_FAMILY } from './titillium-font-face.js';
import { buildTransposedSections } from './transposed.js';

/**
 * The HTML export as one continuous, interactive page (issue #12, spec F2-F4)
 * — `renderExportHtml({ layout: 'continuous' })`.
 *
 * Where the paged document fixes every box so a PDF can be printed from it,
 * this one fixes nothing: there are no page boxes, no tiling and no print
 * breaks, just the whole matrix in one table whose palier headers and row
 * labels stay in view while it scrolls (sticky, both axes).
 *
 * What it reads from `RenderExportHtmlInput`, decision by decision:
 * - **X5 row heights.** `table.rowHeightMode === 'contenu'` puts no height on
 *   any row: the browser sizes each one to its content — the genuine version
 *   of what the PDF has to estimate. `'ecran'` sets `rowHeights` (the
 *   on-screen `viewState` heights) as the row `height`, which a table treats
 *   as a minimum, so content still never clips.
 * - **X6 transpose.** An unpaginated stack of the per-section tables, each
 *   title row a sticky `<thead>` that holds while its section scrolls past.
 * - **X7 dressing.** Header once, at the top, sized to its content (the title
 *   keeps its two-line clamp); sujet/description before or after the table; a
 *   static footer at the end (footer text left, PMS signature right).
 *   `dressing.pageNumbers` and `dressing.assemblyMarks` are ignored even when
 *   true: a state carried over from a PDF preset must not leak a « Page 1 sur
 *   1 » or move the signature into a corner of a document that has none.
 * - **X8 annotations.** Highlights are content-anchored and still render; ink
 *   is dropped, since its projection needs tile geometry and fixed row
 *   heights, which X5 removes.
 *
 * Ignored: `tiles` (every model column and row is rendered, in model order —
 * callers apply column order to the model beforehand), `table.repeatHeaders`,
 * `table.pageBreaksBefore`, and `geometry` except as the nominal width handed
 * to `buildTransposedSections` (whose height estimate this layout never uses).
 */
export function renderContinuousExportHtml(input: RenderExportHtmlInput): string {
  // X8: `highlightedLineKeys` alone — no `projectInkAnnotations`, so ink never
  // reaches the document.
  const highlightKeys = highlightedLineKeys(input.annotations ?? []);

  const { enabled: headerEnabled } = documentHeaderVisibility({
    dressing: input.dressing,
    hasCompanyLogo: input.companyLogoDataUri !== null,
    exportedAt: input.exportedAt,
    matrixUpdatedAt: input.matrixUpdatedAt,
  });
  const header = headerEnabled
    ? `<header class="export-header">${renderDocumentHeaderContent(input)}</header>`
    : '';

  // X7: with page numbers and assembly marks forced off, the footer exists
  // exactly when it has footer text or a signature to carry.
  const footerEnabled = documentFooterEnabled({
    ...input.dressing,
    pageNumbers: false,
    assemblyMarks: false,
  });
  const footer = footerEnabled ? renderFooter(input) : '';

  const subjectDescription = renderSubjectDescriptionBlock(input.subjectDescription);
  const position = input.subjectDescription?.position;
  const content =
    input.table.structure === 'transpose'
      ? renderTransposedSections(input, highlightKeys)
      : renderGridTable(input, highlightKeys);

  // No wrapper of the table may set `overflow` (it would become the sticky
  // cells' scroll container and pin them to a box that never scrolls): the
  // document scrolls as the viewport, on both axes.
  // Issue #19: the notes close the document, before the footer.
  const notes = input.notes ? renderNotesSectionHtml(input.notes) : '';
  const body = `<main class="export-document">${header}${position === 'before' ? subjectDescription : ''}${content}${position === 'after' ? subjectDescription : ''}${notes}${footer}</main>`;

  const style = [
    input.fontFaceCss ?? '',
    continuousStyle(input, { headerEnabled, footerEnabled }),
    input.notes ? notesStyle(input.fontSizePt) : '',
  ]
    .filter((part) => part.length > 0)
    .join(' ');

  return renderHtmlDocument(input.matrixTitle, style, body);
}

function renderFooter(input: RenderExportHtmlInput): string {
  const rawFooterText = input.dressing.footerText;
  const left =
    rawFooterText !== null && rawFooterText.trim().length > 0
      ? `<span>${escapeHtml(rawFooterText)}</span>`
      : '';
  return `<footer class="export-footer"><div class="export-footer-left">${left}</div><div class="export-footer-right">${pmsSignatureHtml(input)}</div></footer>`;
}

/**
 * The whole grid as one table. Its width is set explicitly to the sum of its
 * columns: `table-layout: fixed` only takes effect on a table with a definite
 * width, and without it the automatic algorithm would squeeze a matrix wider
 * than the viewport down to fit it — the opposite of a page meant to scroll.
 */
function renderGridTable(input: RenderExportHtmlInput, highlightKeys: ReadonlySet<string>): string {
  const { model, labelColumnCount, activationLevierNames, columnWidths, rowHeights } = input;
  const tableWidthPx =
    labelColumnCount * ROW_LABEL_WIDTH_PX +
    model.columns.reduce(
      (sum, column) =>
        sum + Math.round(columnWidths[String(column.number)] ?? DEFAULT_COLUMN_WIDTH_PX),
      0,
    );
  const colgroup = renderGridColgroup(model.columns, columnWidths, labelColumnCount, 1);
  const headerRow = renderColumnHeaderRow(
    model.columns,
    labelColumnCount,
    input.appearance,
    input.noyau,
  );

  const activationRowCount = model.rows.filter((row) => row.kind === 'activation').length;
  const firstActivationIndex = model.rows.findIndex((row) => row.kind === 'activation');
  const screenHeights = input.table.rowHeightMode === 'ecran';

  const bodyRows = model.rows
    .map((row, index) => {
      const labels = renderRowLabelCells({
        row,
        isFirstActivationRow: index === firstActivationIndex,
        activationRowCount,
        activationLevierNames,
        labelColumnCount,
        showRowLabels: true,
      });
      const cells = renderDataCells(model, row, model.columns, highlightKeys);
      // X5: `contenu` leaves the row to its content; `ecran` asks for the
      // on-screen height, which a table row treats as a floor, not a clip.
      const height = screenHeights
        ? ` style="height:${Math.round(rowHeights[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX)}px"`
        : '';
      return `<tr${height}>${labels}${cells}</tr>`;
    })
    .join('');

  return `<table class="export-table" style="width:${tableWidthPx}px">${colgroup}<thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
}

/** X6: every section, stacked, each titled by its own sticky `<thead>`. */
function renderTransposedSections(
  input: RenderExportHtmlInput,
  highlightKeys: ReadonlySet<string>,
): string {
  return buildTransposedSections({
    model: input.model,
    fontSizePt: input.fontSizePt,
    density: input.table.density,
    contentWidthPx: input.geometry.pageWidthPx - 2 * input.geometry.marginPx,
  })
    .map((section) =>
      renderTransposedSectionHtml(section, input.appearance, input.noyau, highlightKeys, 'head'),
    )
    .join('');
}

/**
 * Same colour, type and border vocabulary as the paged document (decision
 * F4), re-cut for sticky cells:
 *
 * - `border-collapse: separate` with right/bottom borders on the cells (and
 *   the table's own top/left edge): under `collapse` a border belongs to the
 *   table, not the cell, so it stays behind when a sticky cell moves.
 * - Every sticky cell has an opaque background — body cells scroll *under*
 *   it — including the `white` header theme, which paints nothing inline.
 * - Stacking: row labels (z 1) under column headers (z 2) under the corner
 *   cells, which stick on both axes (z 3).
 * - No page box, no fixed height, no `overflow`, no print rules.
 */
function continuousStyle(
  input: RenderExportHtmlInput,
  furniture: { headerEnabled: boolean; footerEnabled: boolean },
): string {
  const paddingPx = DENSITY_PADDING_PX[input.table.density];
  const border = BORDER_STYLE[input.appearance.borders];
  const borderColor = BORDER_COLOR[input.appearance.borders];
  const tableFontPx = formatPx(input.fontSizePt * CSS_PX_PER_PT);
  const zebra = input.appearance.zebra
    ? ' table.export-table tbody tr:nth-child(even) td { background: #faf9f7; }'
    : '';
  const headerStyle = furniture.headerEnabled
    ? `${DOCUMENT_HEADER_CSS}
  .export-header { margin-bottom: 16px; }`
    : '';
  const footerMarkHeightPx = FOOTER_RESERVED_PX - FOOTER_GAP_PX;
  const footerStyle = furniture.footerEnabled
    ? `
  .export-footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 24px; font-size: ${HEADER_META_PT}pt; color: #6b6a67; }
  .export-footer-right { display: flex; align-items: center; gap: 4px; }
  .export-footer img { display: block; height: ${footerMarkHeightPx}px; width: auto; }`
    : '';
  return `
  body { margin: 0; background: #ffffff; font-family: '${TITILLIUM_FAMILY}', 'Segoe UI', system-ui, -apple-system, sans-serif; }
  .export-document { padding: 24px; }
  .export-subject-description { margin-top: 16px; }${headerStyle}${footerStyle}
  table.export-table, table.export-transposed { border-collapse: separate; border-spacing: 0; table-layout: fixed; line-height: ${LINE_HEIGHT_FACTOR}; font-size: ${tableFontPx}px; border-top: ${border}; border-left: ${border}; }
  table.export-transposed { width: 100%; margin-bottom: 16px; }
  table.export-table th, table.export-table td, table.export-transposed th, table.export-transposed td { border-right: ${border}; border-bottom: ${border}; padding: ${paddingPx}px; text-align: left; vertical-align: top; }${zebra}
  table.export-table thead th { position: sticky; top: 0; z-index: 2; background: #ffffff; }
  table.export-table thead th.export-corner { left: 0; z-index: 3; background: #f7f6f4; }
  table.export-table thead th.export-corner + th.export-corner { left: ${ROW_LABEL_WIDTH_PX}px; }
  table.export-table tbody th { position: sticky; left: 0; z-index: 1; background: #f7f6f4; }
  table.export-table tbody th.export-levier-label { left: ${ROW_LABEL_WIDTH_PX}px; font-weight: 400; }
  table.export-transposed thead th { position: sticky; top: 0; z-index: 2; background: #f7f6f4; }
  th.export-col-header { border-bottom: 2px solid ${borderColor}; height: ${HEADER_ROW_RESERVED_PX}px; }
  th.export-row-label, th.export-transposed-title { font-weight: 600; }
  th.export-transposed-palier { background: #f7f6f4; }
  .export-line-highlight { background: #f7e08a; }
  .export-col-label { display: block; font-size: 1.1em; font-weight: 700; line-height: 1.25; color: #000000; }
  .export-empty { color: #9b9693; font-style: italic; }
  td li { display: list-item; list-style: disc; margin-left: 1.25em; }
  td p { margin: 0; }
`.replace(/\s+/g, ' ');
}
